import { Formik } from 'formik';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { getSelectedCountry } from '../../../../states/slices/resourceSlice';
import {
  sendMIDaccessRequestByEmail} from '../../../../api/requests';
import Input from '../../../../components/form-control/Input';
import Button from '../../../../components/form-control/Button';
import useUserAccount from '../../../../hooks/userAccountHook';
import Loader from '../../../../components/Loader';
import { BrokerInsurerIntegration } from '.';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle } from 'lucide-react';
import Tooltip from '../../../../components/ToolTip';
import { getAllInsurers } from '../../../../services/insurer';
import { showToast } from '../../../../utils/miscFunctions';

interface ICreateIntermediaryProps {
  onClose: () => void;
  onSuccess: () => void;
  intermediary: BrokerInsurerIntegration | undefined;
  isCreateRequest?: boolean;
}
export interface IIntermediaryDTO {
  insurerContactEmails?: string;
}

const SendMIDAccessRequestModal = ({ onClose, onSuccess, intermediary, isCreateRequest }: ICreateIntermediaryProps): JSX.Element => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { user } = useUserAccount();
  const country = useSelector(getSelectedCountry);

  const handleSubmit = async (email: string) => {
    setSubmitting(true);
    if (intermediary) {
      try {
        const response: any = await sendMIDaccessRequestByEmail(intermediary?.id, email);
        showToast.success(response.message || 'Intermediary updated successfully');
        onSuccess();
        onClose();
      } catch (error: any) {
        showToast.error(error.description ?? 'Failed to update intermediary');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const getAllBrollyInsurers = async () => {
    if (!country?.id) return;
    try {
      const response: any = await getAllInsurers(country?.id);
      if (response) {

        const allInsurers = response?.data?.map((insurer: any) => ({
          name: `${insurer.name}`,
          value: Number(insurer.id)
        }));
        return allInsurers;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const { data: insurers, isLoading: insurersLoading } = useQuery({
    queryKey: ['getAllBrollyInsurers'],
    queryFn: getAllBrollyInsurers,
  });

  return (
    <>
      {insurersLoading ? (

        <Loader message="Fetching insurers. Please wait..." brollyLoaderFixed />

      ) : (
        <div className="p-8 max-w-2xl mx-auto">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3 dark:text-darkBg-mainText">Request MID Access</h2>
            <p className="text-gray-600 dark:text-darkBg-mainText">
              You are about to request MID access from {intermediary?.insurerName}.
              Please enter the email address(es) of the underwriter you are requesting the access from.
              You can add multiple email addresses separated by commas.
            </p>
          </div>
          <Formik
            initialValues={{
              insurerContactEmails: intermediary?.insurerContactEmails || '',
            }}
            enableReinitialize={true}
            validateOnChange={true}
            validateOnBlur={true}
            validationSchema={Yup.object().shape({
              insurerContactEmails: Yup.string().trim().required('Email is required'),
            })}
            onSubmit={async (values: any, { resetForm }) => {
              handleSubmit(values.insurerContactEmails);
            }}>
            {({
              values,
              errors,
              isSubmitting,
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              touched
            }) => {
              return (
                <form className="" onSubmit={handleSubmit}>
                  <div className="w-full flex flex-col space-y-8">

                    <Input
                      type="text"
                      name="insurerContactEmails"
                      value={values.insurerContactEmails}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Contact email(s)"
                      placeholder="e.g: bonjour@gmail.com,..."
                      error={touched.insurerContactEmails ? errors.insurerContactEmails : undefined}
                      helperText={touched?.insurerContactEmails ? errors.insurerContactEmails : undefined}
                      appendLabelIcon={
                        <Tooltip
                          placement="bottom"
                          bgColor="bg-black"
                          textColor="text-white"
                          containerClassName="rounded-full"
                          tooltipClassName="w-[200px] text-xs"
                          message={"You can add multiple emails seperated by commas."}>
                          <HelpCircle className='h-3 w-3 ml-2 cursor-pointer' />
                        </Tooltip>
                      }
                    />

                    <div className="w-full flex gap-4 mt-5">
                      <Button
                        label="Cancel"
                        bgColor='secondary'
                        fullWidth
                        onClick={onClose}
                      />
                      <Button
                        label={'Send'}
                        type="submit"
                        className={`h-[50px] !text-md ${submitting || !values.insurerContactEmails ? 'bg-gray-300' : 'bg-primary-main hover:bg-primary-main'} rounded !border-none px-10 !w-1/2`}
                        loading={submitting}
                        disabled={submitting || !values.insurerContactEmails}
                      />
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      )}
    </>
  );
};

export default SendMIDAccessRequestModal;
